import React, { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, FeatureGroup, useMap, Polygon, Marker } from 'react-leaflet'
import BaseMapSwitcher from './BaseMapSwitcher'
import { BASEMAPS } from '../utils/basemaps'
import { useBaseMap } from '../context/BaseMapContext'
import { EditControl } from 'react-leaflet-draw'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

interface MultiPolygonEditorProps {
  title?: string
  initialMultiPolygon?: number[][][] // [[[lat,lng],...],[...]]
  onChange: (multi: number[][][]) => void
  onClose: () => void
  showSaveButton?: boolean
  embedded?: boolean
  onMapReady?: (map: L.Map) => void
  readonly?: boolean
  overlaySiteName?: string
  overlaySite?: number[][][]
  overlayAreas?: Array<{ name: string; multi: number[][][] }>
}

const MultiPolygonEditor: React.FC<MultiPolygonEditorProps> = ({ title = 'Edit Boundaries', initialMultiPolygon, onChange, onClose, showSaveButton = true, embedded = false, onMapReady, readonly = false, overlaySiteName, overlaySite, overlayAreas }) => {
  const [center] = useState<[number, number]>([62.0, 15.0])
  const [zoom] = useState(6)
  const featureGroupRef = useRef<L.FeatureGroup>(null)
  const mapRef = useRef<L.Map | null>(null)
  const [locating, setLocating] = useState(false)
  const { baseId, setBaseId } = useBaseMap()
  const [geoError, setGeoError] = useState<string | null>(null)

  useEffect(() => {
    const fg = featureGroupRef.current
    if (!fg) return
    fg.clearLayers()
    if (initialMultiPolygon) {
      initialMultiPolygon.forEach(poly => {
        if (poly && poly.length >= 3) {
          const p = L.polygon(poly as any, { color: '#3498db', weight: 2, fillOpacity: 0.15 })
          fg.addLayer(p)
        }
      })
    }
  }, [initialMultiPolygon])

  const collect = (): number[][][] => {
    const fg = featureGroupRef.current
    const polys: number[][][] = []
    if (!fg) return polys
    fg.eachLayer((layer: any) => {
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[]
        polys.push(latlngs.map(ll => [ll.lat, ll.lng]))
      }
    })
    return polys
  }

  const onCreated = () => onChange(collect())
  const onEdited = () => onChange(collect())
  const onDeleted = () => onChange(collect())

  const centerOnMe = () => {
    setGeoError(null)
    setLocating(true)
    if (!('geolocation' in navigator)) {
      setGeoError('Geolocation is not supported by your browser.')
      setLocating(false)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        const map = mapRef.current || (featureGroupRef.current as any)?._map
        if (map) {
          map.setView([latitude, longitude], 15) // zoom in on user
        }
        setLocating(false)
      },
      (err) => {
        setGeoError('Unable to get your location. Check permissions.')
        setLocating(false)
        console.error('Geolocation error:', err)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    )
  }

const MapReady: React.FC<{ onReady?: (m: L.Map) => void }> = ({ onReady }) => {
    const map = useMap()
    React.useEffect(() => {
      if (onReady) onReady(map)
    }, [map, onReady])
    return null
  }

  const mapEl = (
    <>
      {geoError && <div className="error" style={{ margin: '0.5rem 0' }}>{geoError}</div>}
      <MapContainer center={center} zoom={zoom} style={{ height: '75vh', width: '100%' }}>
        <TileLayer key={baseId} url={BASEMAPS[baseId].url} attribution={BASEMAPS[baseId].attribution} {...(BASEMAPS[baseId].options || {})} />
        <MapReady onReady={onMapReady} />
        {!readonly && (
          <FeatureGroup ref={featureGroupRef as any}>
            <EditControl
              position="topright"
              onCreated={onCreated as any}
              onEdited={onEdited as any}
              onDeleted={onDeleted as any}
              draw={{ rectangle: false, circle: false, circlemarker: false, marker: false, polyline: false, polygon: true }}
            />
          </FeatureGroup>
        )}
        {/* Overlays for overview/read-only context; draw site polygons, label placed north of bounds */}
        {overlaySite && overlaySite.map((poly, idx) => (
          <Polygon
            key={`site-${idx}`}
            positions={poly as any}
            pathOptions={{ color: '#3498db', weight: 2, opacity: 0.7, fillColor: '#3498db', fillOpacity: 0.08 }}
            interactive={false}
          />
        ))}
        {overlaySiteName && overlaySite && (() => {
          const bounds = computeBounds(overlaySite)
          if (!bounds) return null
          const latSpan = Math.max(0.0005, bounds.maxLat - bounds.minLat)
          const offset = Math.max(0.001, latSpan * 0.06)
          const pos: [number, number] = [bounds.maxLat + offset, (bounds.minLng + bounds.maxLng) / 2]
          return <Marker position={pos as any} icon={L.divIcon({ className: 'poly-label poly-label-site poly-label-site--pill', html: overlaySiteName })} interactive={false} />
        })()}
        {overlayAreas && overlayAreas.flatMap((area, aidx) => (
          area.multi.map((poly, pidx) => {
            const centroid = computeCentroid(poly)
            return (
              <React.Fragment key={`area-${aidx}-${pidx}`}>
                <Polygon
                  positions={poly as any}
                  pathOptions={{ color: '#e74c3c', weight: 2, opacity: 0.7, fillColor: '#e74c3c', fillOpacity: 0.12 }}
                  interactive={false}
                />
                {centroid && (
                  <Marker position={centroid as any} icon={L.divIcon({ className: 'poly-label poly-label-area', html: area.name })} interactive={false} />
                )}
              </React.Fragment>
            )
          })
        ))}
      </MapContainer>
    </>
  )

  if (embedded) {
    return mapEl
  }

  return (
    <div className="polygon-editor-overlay">
      <div className="polygon-editor-container">
        <div className="polygon-editor-header">
          <h3>{title}</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn" onClick={centerOnMe} disabled={locating}>
              {locating ? '📍 Locating…' : '📍 Center on Me'}
            </button>
            <BaseMapSwitcher value={baseId} onChange={setBaseId} />
            {showSaveButton && (
              <button className="btn btn-success" onClick={onClose}>💾 Save</button>
            )}
          </div>
        </div>
        {mapEl}
      </div>
    </div>
  )
}

function computeCentroid(poly: number[][]): [number, number] | null {
  try {
    if (!Array.isArray(poly) || poly.length === 0) return null
    let x = 0, y = 0
    poly.forEach(([lat, lng]) => { x += lat; y += lng })
    return [x / poly.length, y / poly.length]
  } catch {
    return null
  }
}

export default MultiPolygonEditor

function computeBounds(multi: number[][][]): { minLat: number; maxLat: number; minLng: number; maxLng: number } | null {
  try {
    let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
    ;(multi || []).forEach(poly => {
      (poly || []).forEach(([lat, lng]) => {
        if (lat < minLat) minLat = lat
        if (lng < minLng) minLng = lng
        if (lat > maxLat) maxLat = lat
        if (lng > maxLng) maxLng = lng
      })
    })
    if (!isFinite(minLat) || !isFinite(minLng) || !isFinite(maxLat) || !isFinite(maxLng)) return null
    return { minLat, maxLat, minLng, maxLng }
  } catch {
    return null
  }
}
